import React, { useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import {Autocomplete, TextField} from '@mui/material';
import { useAddNewConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import useAuth from '../../../../features/hooks/useAuth';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useNavigate } from "react-router-dom";
import { HeadingWrap, Span, CommonButton } from '../../../../Styles/CommonStyles';
import nameStringFn from '../../../../data/nameStringFn';
import UnivConsumSelector from '../UnivConsumSelector';

const Container = styled.div`
  margin-left: 10px;
  margin-right: 10px;
`
const Issuebutton = styled(CommonButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 250px;
  height: 50px;
  background-color: teal;
  margin-right: 30px;
  cursor: pointer;
  border-radius: 10px; 
`
const SingleSelectCont = styled.div`
  margin-top: 20px;
  margin-left: 20px;
  margin-bottom: 40px;
  width: 30%;
`
const RemarkContainer = styled.div`
  margin-top: 15px;
  margin-left:10px;
  margin-bottom: 20px;
  width: 90%;
`;

const SubHeadingField = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 20px;
  margin-top: 20px;
  margin-bottom: 15px;
  font-family: "Roboto","Helvetica","Arial",sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #5C5891;  
`

const BoToStoreConsumReq = () => {
  const [remark, setRemark] = useState('');  
  const [addSpecIndex, setAddSpecIndex] = useState(1);  
  const [storeSelectedValue, setStoreSelectedValue] = useState('');
  const [storeOptions, setStoreOptions] = useState([]);
  const [selectedConsum, setSelectedConsum] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  
  // logged in user data
  const { id, email, designation, designationSuffix, numSuffix, roles } = useAuth();
  const user_current_office = useSelector((state) => state.office.off);

  // API queries
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { offices } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      offices: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // Memoize useroffice to prevent unnecessary recalculations
  const useroffice = useMemo(() => {
    return offices && Object.values(offices || {})?.find(elm => elm.name === user_current_office);
  }, [offices, user_current_office]);

  // Memoize Bo_id and UnitHead_id
  const Bo_id = useMemo(() => useroffice?.Bo, [useroffice]);
  const UnitHead_id = useMemo(() => useroffice?.unitHead, [useroffice]);

  // create sending userobject - memoized to avoid recreating on every render
  const sendBoObj = useMemo(() => ({ 
    id: UnitHead_id, 
    name: getSingleuser && getSingleuser[UnitHead_id]?.name, 
    office: user_current_office, 
    designation: getSingleuser && (getSingleuser[UnitHead_id]?.designation?.[user_current_office] || 'Unknown'), 
    designationSuffix: getSingleuser && (getSingleuser[UnitHead_id]?.designationSuffix?.[user_current_office] || ''), 
    numSuffix: getSingleuser && (getSingleuser[UnitHead_id]?.numSuffix?.[user_current_office] || '') 
  }), [UnitHead_id, getSingleuser, user_current_office]);

  // Populate storeOptions only when storeoffices changes
  useEffect(() => {
    if (storeoffices) {
      const options = Object.values(storeoffices)
        .map(office => office.storeoffice);
      setStoreOptions(options);
    }
  }, [storeoffices]);
  
  const [addNewConsumableReq, { isLoading: isAddingReq }] = useAddNewConsumableReqMutation();
 
  // Memoize validation functions to prevent recreation on each render
  const hasValidQuantity = useCallback((data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return false;
    }
  
    return data.every(item => {
      const quantity = parseInt(item.quantity, 10);
      return !isNaN(quantity) && quantity > 0;
    });
  }, []);
  
  // Memoize uniqueReqID to prevent unnecessary regeneration
  const uniqueReqID = useCallback(() => {
    const dateString = Date.now().toString(36);
    const emailofuser = getSingleuser && getSingleuser[id]?.email 
      ? getSingleuser[id].email.slice(0, -8) 
      : 'user';
    return `cons-${emailofuser}-${dateString}`;
  }, [getSingleuser, id]);
  
  // Generate uniqueReqId only when needed, not on every render
  const uniqueReqId = useMemo(() => uniqueReqID(), [uniqueReqID]);
  
  // Memoize the userReqData to prevent unnecessary object recreations
  const userReqData = useMemo(() => ({
    uniqueReqId,
    req_type: "BoToStoreConsumIssue",
    req_creator_id: id,
    req_creator_details: `${nameStringFn(getSingleuser?.[id] || {})}-${user_current_office}-${designation[user_current_office] || ''}`,
    req_store: storeSelectedValue,
    consumables_requested:[{ 
      time_stamp: new Date(), 
      actionNo: 1, 
      data: selectedConsum.map(item => ({
        ...item,
        asset: item?.asset? item.asset.trim() : "Not Applicable",
        assettag: item?.assettag ? item.assettag : {label: "Not Applicable", value: "Not Applicable"},
      }))
    }],
    bo_partial_issue: false,
    bo_consum_issue:[],
    store_partial_issue: false,
    store_consum_issue:[],
    curr_status: 'Pending',
    created_at: new Date(),
    curr_sent_by: id,
    curr_sent_to: UnitHead_id,
    curr_sent_to_string: `${nameStringFn(getSingleuser?.[UnitHead_id] || {})}-${user_current_office}-${getSingleuser?.[UnitHead_id]?.designation?.[user_current_office] || 'Unknown'}`,
    curr_sent_to_tag: 'botostore_consreq_created',
    flow_users:[id],
    req_data: [{
      sent_to_tag:"botostore_consreq_created",
      Text: remark,
      sent_by: { 
        id, 
        name: getSingleuser && getSingleuser[id]?.name, 
        office: user_current_office, 
        designation: designation[user_current_office] || '', 
        designationSuffix: designationSuffix[user_current_office] || '', 
        numSuffix: numSuffix[user_current_office] || '' 
      },
      sent_to: sendBoObj,
      action:'New Consumable Issue Request Created',
      time_stamp: new Date()
    }],
    opened: false
  }), [
    uniqueReqId, 
    id, 
    getSingleuser, 
    user_current_office, 
    designation,
    storeSelectedValue, 
    selectedConsum, 
    UnitHead_id, 
    remark, 
    sendBoObj,
    designationSuffix,
    numSuffix
  ]);
  
  // Handle form submission with useCallback to prevent recreation
  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!storeSelectedValue) {
      alert("Please select a store");
      return;
    }
    
    if (!hasValidQuantity(selectedConsum)) {
      alert("Please enter valid quantities for all items");
      return;
    }
    
    if (!UnitHead_id) {
      alert("Unit Head information not found");
      return;
    }
    
    if (!getSingleuser || !getSingleuser[id]) {
      alert("User details not available");
      return;
    }
    
    try {
      setIsSubmitting(true);      
      await addNewConsumableReq(userReqData).unwrap();
      alert("Request submitted successfully");
      
      // Clear form data
      setSelectedConsum([]);
      setRemark('');
      setAddSpecIndex(1);
      
      // Navigate to inbox or relevant page
      navigate('/management/consumables');
      
    } catch (error) {
      console.error("Failed to submit request", error);
      alert("Failed to submit request. Please try again: " + (error.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    storeSelectedValue, 
    hasValidQuantity, 
    selectedConsum, 
    UnitHead_id, 
    getSingleuser, 
    id, 
    addNewConsumableReq, 
    userReqData, 
    navigate
  ]);

  // Memoize the handler for the store selection
  const handleStoreChange = useCallback((event, newValue) => {
    setStoreSelectedValue(newValue);
  }, []);

  // Memoize the handler for the remark change
  const handleRemarkChange = useCallback((e) => {
    setRemark(e.target.value);
  }, []);

  return (
    <Container>
      <HeadingWrap>
        <Span>Request Consumable Issue for BO</Span>
      </HeadingWrap>

      <SingleSelectCont>
        <Autocomplete
          fullWidth
          size="small"
          options={storeOptions}
          value={storeSelectedValue || null}
          onChange={handleStoreChange}
          renderInput={(params) => <TextField {...params} label="Please Select Store" />}
          isOptionEqualToValue={(option, value) => {
            // Handle null and empty values
            if (!value) return true;
            return option === value;
          }}
        />
      </SingleSelectCont>

      {storeSelectedValue?.length ? (
        <SubHeadingField>Please Select Consumable(s) for issue</SubHeadingField>
      ):null}

      {storeSelectedValue?.length ? (
        <form onSubmit={handleFormSubmit} style={{marginTop:"20px", marginLeft:"8px"}}>
          <UnivConsumSelector
            addSpecIndex={addSpecIndex}
            setAddSpecIndex={setAddSpecIndex}
            selectedConsum={selectedConsum}
            setSelectedConsum={setSelectedConsum}
            storeSelectedValue={storeSelectedValue}
            hidePartNumber={true}
          />

          {hasValidQuantity(selectedConsum) && (
            <>
              <RemarkContainer>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Add Remark"
                  variant="outlined"
                  value={remark}
                  onChange={handleRemarkChange}
                />
              </RemarkContainer>
              <Issuebutton 
                type='submit' 
                disabled={isAddingReq || isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request to Unit Head'}
              </Issuebutton>
            </>
          )}
        </form>
      ):null}
      
    </Container>
  );
};

export default BoToStoreConsumReq;
